// AllLibrarian.js
import React, { useEffect, useState } from "react";
import { GoPlus } from "react-icons/go";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Skeleton, Card, Row, Col } from "antd";

import Layout from "../../../../Components/Common/Layout";
import DashLayout from "../../../../Components/Admin/AdminDashLayout";
import SidebarSlide from "../../../../Components/Common/SidebarSlide";
import Spinner from "../../../../Components/Common/Spinner";
import CreateRole from "../../../../Components/Common/RBAC/CreateRole";
import NoDataFound from "../../../../Components/Common/NoDataFound";
import { fetchAllStaff } from "../../../../Store/Slices/Admin/Users/Staff/staff.action";
import useNavHeading from "../../../../Hooks/CommonHooks/useNavHeading ";
import Header from "../Component/Header";
import { getAllRolesThunk } from "../../../../Store/Slices/Common/RBAC/rbacThunks";
import ProtectedSection from "../../../../Routes/ProtectedRoutes/ProtectedSection";
import { PERMISSIONS } from "../../../../config/permission";
import ProtectedAction from "../../../../Routes/ProtectedRoutes/ProtectedAction";
import { fetchDriverList } from "../../../../Store/Slices/Transportation/Driver/driver.action";
import AddDriver from "./AddDriver";
import ViewDriver from "./ViewDriver";
import ProfileDriverCard from "../SubComponents/ProfileDriverCard";
import CardShimmer from "../../../../Components/Common/CardShimmer";

const AllDriver = () => {
  // const { t } = useTranslation("admAccounts");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarContent, setSidebarContent] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverData, setDriverData] = useState(null);
  const [sortOption, setSortOption] = useState(null);
  const [filterRoles, setFilterRoles] = useState([]);
  const [sortedDrivers, setSortedDrivers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useTranslation("admUserProfile");
  const { drivers, loading: driverLoading } = useSelector(
    (store) => store.transportation.transportDriver
  );
  const role = useSelector((store) => store.common.auth.role);
  const { roles: AllRoles } = useSelector((state) => state.admin.rbac);

  useEffect(() => {
    dispatch(fetchDriverList());
    dispatch(getAllRolesThunk());
  }, [dispatch]);

  useEffect(() => {
    setSortedDrivers(drivers);
  }, [drivers]);

  useEffect(() => {
    let filtered = [...drivers];

    // 🔍 SEARCH
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (driver) =>
          driver?.fullName?.toLowerCase().includes(query) ||
          driver?.email?.toLowerCase().includes(query)
      );
    }

    // 🎯 FILTER BY ROLE
    if (filterRoles.length > 0) {
      filtered = filtered.filter((driver) =>
        driver.position?.some((pos) => filterRoles.includes(pos))
      );
    }

    // ↕️ SORT
    if (sortOption) {
      switch (sortOption) {
        case "by_date":
          filtered.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          break;
        case "by_roles":
          filtered.sort((a, b) => a.position.length - b.position.length);
          break;
        default:
          break;
      }
    }

    setSortedDrivers(filtered);
  }, [searchQuery, sortOption, filterRoles, drivers]);

  const handleSidebarOpen = (content, data = null) => {
    setSidebarContent(content);
    setSelectedDriver(data);
    setDriverData(data);
    setSidebarOpen(true);
  };

  const handleSidebarClose = () => setSidebarOpen(false);

  const editUser = (event, data) => {
    event.stopPropagation();
    handleSidebarOpen("editDriver", data);
  };

  const handleDriverClick = (driverMember) => {
    handleSidebarOpen("viewDriver", driverMember);
  };

  const handleAddDriverClick = () => {
    handleSidebarOpen("addDriver");
    setDriverData(null);
  };

  const driverRoles =
    AllRoles?.filter(
      (dept) => dept.department.toLowerCase() === "driver"
    )?.flatMap((dept) => dept.roles) || [];

  const sortOptions = [
    { label: "By Date", value: "by_date" },
    { label: "By Roles", value: "by_roles" },
  ];

  const filterOptionsList = driverRoles.map((roleItem) => ({
    label: roleItem.name,
    value: roleItem.name,
  }));

  const handleSortFilterApply = ({ sortOption, filterOptions }) => {
    setSortOption(sortOption);
    setFilterRoles(filterOptions);
  };

  const navigateToManageRoles = () => {
    navigate("/users/manage-roles", {
      state: { department: "driver" },
    });
  };

  const handleCreateRole = () => {
    handleSidebarOpen("createRole");
  };

  useNavHeading("User", "Drivers");

  const renderSidebarContent = () => {
    switch (sidebarContent) {
      case "viewDriver":
        return <ViewDriver driver={selectedDriver} />;
      case "addDriver":
        return <AddDriver role="staff" onClose={() => handleSidebarClose()} />;
      case "editDriver":
        return (
          <AddDriver
            role="staff"
            data={driverData}
            onClose={() => handleSidebarClose()}
          />
        );
      case "createRole":
        return <CreateRole onClose={handleSidebarClose} department="staff" />;
      default:
        return <div>{t("Select an action")}</div>;
    }
  };

  return (
    <Layout title={t("All Drivers")}>
      <DashLayout>
        {driverLoading ? (
          <div className="bg-gray-50 min-h-screen p-4">
           <CardShimmer rows={3} columns={4}/>
          </div>
        ) : (
          <ProtectedSection
            requiredPermission={PERMISSIONS.VIEW_DRIVER_LIST}
            title={"All Drivers"}
          >
            <div className="p-4 bg-gray-50 min-h-screen relative">
              <Header
                title={t("All Drivers")}
                count={drivers?.length || 0}
                sortOptions={sortOptions}
                filterOptions={filterOptionsList}
                department="Drivers"
                onSortFilterApply={handleSortFilterApply}
                navigateToManageRoles={navigateToManageRoles}
                handleCreateRole={handleCreateRole}
                isAdmin={role === "admin"}
                currentSort={sortOption}
                currentFilters={filterRoles}
                searchValue={searchQuery}
                onSearchChange={setSearchQuery}
              />

              <div className="flex flex-wrap -mx-2">
                {sortedDrivers?.length > 0 ? (
                  sortedDrivers.map((driver) => (
                    <ProfileDriverCard
                      key={driver._id}
                      profile={driver}
                      onClick={() => handleDriverClick(driver)}
                      editUser={editUser}
                    />
                  ))
                ) : (
                  <div className="flex w-full text-gray-500 h-[90vh] items-center justify-center flex-col text-2xl">
                    <NoDataFound />
                  </div>
                )}
              </div>

              <ProtectedAction requiredPermission={PERMISSIONS.ADD_DRIVER}>
                <button
                  onClick={handleAddDriverClick}
                  className="fixed bottom-8 end-8 btn-gradient  w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:opacity-90 transition duration-200"
                  aria-label="Add New Driver"
                >
                  <GoPlus className="text-2xl" />
                </button>
              </ProtectedAction>
            </div>
          </ProtectedSection>
        )}
      </DashLayout>

      <SidebarSlide
        key={sidebarContent}
        isOpen={isSidebarOpen}
        onClose={handleSidebarClose}
        title={
          <span className="bg-gradient-to-r from-pink-500 to-purple-500 inline-block text-transparent bg-clip-text">
            {sidebarContent === "viewDriver"
              ? t("Quick View of Driver")
              : sidebarContent === "createRole"
              ? t("Create New Role")
              : driverData
              ? t("Edit Driver")
              : t("Add Driver")}
          </span>
        }
        width={
          sidebarContent === "viewDriver"
            ? "30%"
            : sidebarContent === "createRole"
            ? "60%"
            : "75%"
        }
        height="100%"
      >
        {renderSidebarContent()}
      </SidebarSlide>
    </Layout>
  );
};

export default AllDriver;
